
import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"

import { cn } from "@/lib/utils"

interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  isDevelopTimer?: boolean;
  markers?: number[]; // Array of percentage values where markers should appear
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, isDevelopTimer, markers = [], ...props }, ref) => {
  // Helper function to interpolate between colors based on percentage
  const getColorForPercentage = (percentage: number): string => {
    // Define our color stops
    const colors = {
      0: { r: 239, g: 68, b: 68 },    // Red (#ef4444)
      50: { r: 251, g: 191, b: 36 },  // Yellow (#fbbf24)
      100: { r: 34, g: 197, b: 94 }   // Green (#22c55e)
    };

    if (percentage <= 0) return '#ef4444';
    if (percentage >= 100) return '#22c55e';

    // Find which color pair to interpolate between
    let lower, upper;
    if (percentage <= 50) {
      lower = colors[0];
      upper = colors[50];
      // Adjust percentage to be between 0-1 for this range
      percentage = percentage * 2;
    } else {
      lower = colors[50];
      upper = colors[100];
      // Adjust percentage to be between 0-1 for this range
      percentage = (percentage - 50) * 2;
    }

    // Interpolate between the colors
    const r = Math.round(lower.r + (upper.r - lower.r) * (percentage / 100));
    const g = Math.round(lower.g + (upper.g - lower.g) * (percentage / 100));
    const b = Math.round(lower.b + (upper.b - lower.b) * (percentage / 100));

    return `rgb(${r}, ${g}, ${b})`;
  };

  // For 0%, fill the bar 100% red. Otherwise, normal logic.
  const displayValue = value === 0 ? 100 : value || 0;
  
  // If in development timer mode, always use red color
  const displayColor = isDevelopTimer 
    ? '#ef4444' 
    : (value === 0 ? '#ef4444' : getColorForPercentage(value || 0));
    
  // Determine if dark mode is active
  const isDarkroomMode = document.body.classList.contains('darkroom-mode');

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-4 w-full overflow-hidden rounded-full bg-muted",
        className
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className="h-full w-full flex-1 transition-all duration-300"
        style={{
          width: `${displayValue}%`,
          backgroundColor: displayColor
        }}
      />
      
      {/* Agitation markers - now more visible but not pulsing */}
      {markers.length > 0 && markers.map((marker, index) => {
        // Use darker red color for markers in dark room mode, white otherwise
        const markerColor = isDarkroomMode ? "#bf616a" : "white";
        
        return (
          <div 
            key={`marker-${index}`}
            className={cn(
              "absolute top-0 h-full z-10", // Full height marker
              marker <= (value || 0) ? "bg-opacity-50" : "bg-opacity-100" // Dim passed markers
            )}
            style={{ 
              left: `${marker}%`,
              width: "2px", // Wider for better visibility
              boxShadow: isDarkroomMode 
                ? '0 0 8px rgba(191, 97, 106, 0.9), 0 0 4px rgba(191, 97, 106, 0.9)' 
                : '0 0 4px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 0.5)',
              backgroundColor: markerColor
            }}
            title={`Agitation at ${marker}%`}
          />
        );
      })}
    </ProgressPrimitive.Root>
  )
})
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
